// src/components/layout/mobile/BottomTabBar.tsx
import { useLocation, useNavigate } from 'react-router-dom';
import { Sun, Ticket, Plus, Bell, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BottomTabBarProps {
  notificationCount: number;
  onCreatePress: () => void;
}

interface TabDef {
  id: string;
  icon: React.ElementType;
  label: string;
  path?: string;          // undefined = custom action (create button)
  matchPaths?: string[];  // paths that activate this tab
}

const tabs: TabDef[] = [
  {
    id: 'hoy',
    icon: Sun,
    label: 'Hoy',
    path: '/actividades',
    matchPaths: ['/actividades'],
  },
  {
    id: 'tickets',
    icon: Ticket,
    label: 'Tickets',
    path: '/tickets',
    matchPaths: ['/tickets'],
  },
  {
    id: 'create',
    icon: Plus,
    label: 'Crear',
    // no path — handled by onCreatePress
  },
  {
    id: 'notificaciones',
    icon: Bell,
    label: 'Alertas',
    path: '/configuracion/preferencias', // TODO: dedicated notifications page
    matchPaths: [],
  },
  {
    id: 'mas',
    icon: MoreHorizontal,
    label: 'Más',
    path: '/mas',
    matchPaths: ['/mas', '/dashboard', '/conversaciones', '/clientes', '/proyectos', '/kanban', '/conocimiento', '/reportes', '/dashboards', '/configuracion'],
  },
];

function isActive(tab: TabDef, pathname: string): boolean {
  if (!tab.matchPaths) return false;
  return tab.matchPaths.some((p) => pathname.startsWith(p));
}

export function BottomTabBar({ notificationCount, onCreatePress }: BottomTabBarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <nav
      className="sticky bottom-0 z-40 flex items-center justify-around h-bottom-bar bg-background/95 backdrop-blur border-t border-border safe-area-bottom"
      role="tablist"
    >
      {tabs.map((tab) => {
        const active = isActive(tab, location.pathname);
        const Icon = tab.icon;
        const isCreate = tab.id === 'create';

        if (isCreate) {
          return (
            <button
              key={tab.id}
              onClick={onCreatePress}
              className="flex items-center justify-center w-12 h-12 -mt-3 rounded-full bg-primary text-primary-foreground shadow-lg active:scale-95 transition-transform"
              aria-label="Crear"
            >
              <Plus className="h-6 w-6" />
            </button>
          );
        }

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={active}
            onClick={() => tab.path && navigate(tab.path)}
            className={cn(
              'flex flex-col items-center justify-center min-w-touch min-h-touch gap-0.5 rounded-md transition-colors active:bg-muted/50',
              active ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <div className="relative">
              <Icon className="h-5 w-5" />
              {tab.id === 'notificaciones' && notificationCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </div>
            {active && (
              <span className="text-[10px] font-medium leading-none">{tab.label}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
